import * as React from 'react';
import {
  useFloating,
  useDismiss,
  useRole,
  useInteractions,
  type UseFloatingOptions,
} from '@floating-ui/react';

import {emptyObject} from '@packages/utilities/fp/utils';
import {useDevAlert} from '@packages/systems/spring-hooks';

import {useLegacyOverlayCoordinator} from '../../hooks';

export type UseDialogOptions = {
  initialOpen?: boolean;
  open?: boolean;
  onOpenChange?: UseFloatingOptions['onOpenChange'];
  shouldHideOnClickOutside?: boolean;
  shouldHideOnEsc?: boolean;
  isNonModalDialog?: boolean;
  id?: string;
};

export function useFloatingDialog({
  open,
  onOpenChange: setOpen,
  shouldHideOnClickOutside,
  shouldHideOnEsc,
  isNonModalDialog,
}: UseDialogOptions = emptyObject) {
  const [labelId, setLabelId] = React.useState<string | undefined>();
  const [descriptionId, setDescriptionId] = React.useState<
    string | undefined
  >();
  const {nodeId, onOutsidePress, openLegacyOverlays} =
    useLegacyOverlayCoordinator();

  const data = useFloating({
    open,
    onOpenChange: setOpen,
    nodeId,
  });

  const context = data.context;

  useDevAlert(
    Boolean(shouldHideOnClickOutside) && Boolean(isNonModalDialog),
    'Do not use both shouldHideOnClickOutside and isNonModalDialog together.'
  );

  let outsidePress: boolean = true;

  if (
    (shouldHideOnClickOutside && isNonModalDialog) ||
    shouldHideOnClickOutside === false
  ) {
    outsidePress = false;
  }

  const dismiss = useDismiss(context, {
    escapeKey: shouldHideOnEsc !== false && openLegacyOverlays.length === 0,
    outsidePress: outsidePress ? onOutsidePress : false,
    bubbles: false,
  });

  const role = useRole(context);

  const interactions = useInteractions([dismiss, role]);

  return React.useMemo(
    () => ({
      open,
      setOpen,
      ...interactions,
      ...data,
      labelId,
      descriptionId,
      setLabelId,
      setDescriptionId,
      nodeId,
    }),
    [open, setOpen, interactions, data, labelId, descriptionId, nodeId]
  );
}

export type UseDialog = ReturnType<typeof useFloatingDialog>;
